/**
 * ЕДИНЫЙ СЛОВАРЬ СВЕТОФОР-СТАТУСОВ (capability design-tokens).
 *
 * Два уровня:
 *  - PaletteVariant — низкоуровневые визуальные варианты (публичный API примитивов Badge/StatusDot).
 *  - StatusRole — семантические роли, через которые доменные компоненты выражают статус.
 *
 * Доменный статус -> роль -> вариант палитры. token-id роли НЕ равен отображаемой подписи:
 * подписи берутся из макета и capability service-training (см. cabinet-design-sync D1а).
 */

/** Визуальные варианты палитры (как классы .b-* / .d-* макета). */
export type PaletteVariant = 'red' | 'amber' | 'green' | 'gray' | 'blue'

/** Семантические роли статуса. */
export type StatusRole = 'danger' | 'warning' | 'success' | 'neutral' | 'info'

/** Доменные статусы предметной области (token-id, не подпись). */
export type DomainStatus = 'overdue' | 'expiring' | 'ok' | 'none' | 'in-progress'

/** Роль -> вариант палитры. */
export const ROLE_TO_VARIANT: Record<StatusRole, PaletteVariant> = {
  danger: 'red',
  warning: 'amber',
  success: 'green',
  neutral: 'gray',
  info: 'blue',
}

/** Доменный статус -> семантическая роль. */
export const DOMAIN_STATUS_TO_ROLE: Record<DomainStatus, StatusRole> = {
  overdue: 'danger', // просрочено
  expiring: 'warning', // истекает-скоро
  ok: 'success', // в-порядке / в-силе
  none: 'neutral', // нет-данных
  'in-progress': 'info', // в-процессе
}

/** Доменный статус -> вариант палитры (производное). */
export function domainStatusToVariant(status: DomainStatus): PaletteVariant {
  return ROLE_TO_VARIANT[DOMAIN_STATUS_TO_ROLE[status]]
}

/**
 * Приоритет «худшего» статуса (red > amber > green > gray) — для цвета строки TrafficRow
 * по всем направлениям, включая скрытые за «+N»/«Прочее». Меньший индекс = хуже.
 */
export const VARIANT_SEVERITY: Record<Exclude<PaletteVariant, 'blue'>, number> = {
  red: 0,
  amber: 1,
  green: 2,
  gray: 3,
}

/** Худший (наиболее тревожный) вариант из набора по приоритету red > amber > green > gray. */
export function worstVariant(
  variants: Array<Exclude<PaletteVariant, 'blue'>>,
): Exclude<PaletteVariant, 'blue'> {
  if (variants.length === 0) return 'gray'
  return variants.reduce((worst, v) => (VARIANT_SEVERITY[v] < VARIANT_SEVERITY[worst] ? v : worst))
}
